/**
 * UsageBar Component
 *
 * Visualizes usage capacity as a row of vertical lines.
 * - Gray lines: unused capacity
 * - Purple-500 (#a855f7): used / covered by membership or pass
 * - Purple-300 (#d8b4fe): overage (exceeds limit)
 *
 * Uses inline styles so it renders correctly in any Tailwind config.
 *
 * @example
 * ```tsx
 * <UsageBar
 *   used={90}
 *   thisBooking={30}
 *   overage={60}
 *   total={180}
 * />
 * ```
 */

import { ClockIcon } from '#icons'

const LINE_COUNT = 45
const COLORS = {
  unused: '#E9EAEB',    // butter-gray-200
  used: '#a855f7',      // purple-500 (brand)
  overage: '#d8b4fe',   // purple-300
}

export interface UsageBarProps {
  /** Minutes already used before this booking */
  used: number
  /** Minutes this booking adds (covered portion) */
  thisBooking: number
  /** Minutes of overage (exceeds limit) */
  overage?: number
  /** Total capacity in minutes (the limit). 0 or null = unlimited */
  total: number | null
  /** Remove border and padding — for embedding inside a card that provides its own container */
  borderless?: boolean
  /** Override color for used + thisBooking segments. Defaults to brand purple (#a855f7). Pass `'var(--theme-500)'` from theme-aware contexts. */
  usedColor?: string
  /** Override color for the overage segment. Defaults to a lighter purple (#d8b4fe). Pass `'var(--theme-300)'` from theme-aware contexts. */
  overageColor?: string
  /** Compact mode — fewer segments so they don't blend together in narrow containers (mobile). */
  compact?: boolean
  /**
   * Bar style. `segmented` (default) is the tick-mark row with a leading
   * clock icon. `continuous` is a single rounded track with no icon, for
   * cards that already label the row themselves.
   */
  variant?: 'segmented' | 'continuous'
}

export function UsageBar({
  used,
  thisBooking,
  overage = 0,
  total,
  borderless = false,
  usedColor = COLORS.used,
  overageColor = COLORS.overage,
  compact = false,
  variant = 'segmented',
}: UsageBarProps) {
  const lineCount = compact ? 30 : LINE_COUNT

  // If unlimited or no total, show all as used
  const capacity = total && total > 0 ? total : Math.max(used + thisBooking + overage, 1)

  // ── Continuous variant — one rounded track, no icon ──────────────
  if (variant === 'continuous') {
    const pct = (value: number) => `${Math.min(100, (value / capacity) * 100)}%`

    return (
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: compact ? 6 : 8,
          borderRadius: 9999,
          overflow: 'hidden',
          backgroundColor: COLORS.unused,
        }}
      >
        <div style={{ width: pct(used), backgroundColor: usedColor }} />
        <div style={{ width: pct(thisBooking), backgroundColor: usedColor }} />
        <div style={{ width: pct(overage), backgroundColor: overageColor }} />
      </div>
    )
  }

  // Calculate how many lines each segment gets
  const usedLines = Math.round((used / capacity) * lineCount)
  const bookingLines = Math.round((thisBooking / capacity) * lineCount)
  const overageLines = Math.round((overage / capacity) * lineCount)
  const unusedLines = Math.max(0, lineCount - usedLines - bookingLines - overageLines)

  // Build the line color array
  const lines: string[] = [
    ...Array(usedLines).fill(usedColor),
    ...Array(bookingLines).fill(usedColor),
    ...Array(overageLines).fill(overageColor),
    ...Array(unusedLines).fill(COLORS.unused),
  ].slice(0, lineCount)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 8 : 6,
        ...(borderless ? {} : {
          padding: compact ? 12 : '14px 16px',
          border: '1px solid #e5e7eb',
          borderRadius: compact ? 12 : 16,
        }),
      }}
    >
      {/* Clock icon — butterUI */}
      <div style={{ flexShrink: 0, color: '#414651' }}>
        <ClockIcon className={compact ? 'w-5 h-5' : 'w-4 h-4'} />
      </div>

      {/* Lines */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flex: 1,
        }}
      >
        {lines.map((color, i) => (
          <div
            key={i}
            style={{
              width: 4,
              height: compact ? 16 : 14,
              borderRadius: 2,
              backgroundColor: color,
              flexShrink: 0,
            }}
          />
        ))}
      </div>
    </div>
  )
}
